"use strict";

import { ItemClassInfoResolver } from 'api/assetAPICall';
import { refreshBlockedPairs } from 'api/rpcAPICall';
import { ClassByHashnameResolver } from 'api/tradeAPICall';
import ApplicationEvents from 'components/applicationEvents';
import ViewTradeDialog from 'components/assetPage';
import { CredentialsIcon } from 'components/credentialsIcon';
import { DialogHolder } from 'components/dialogHolder';
import { EventLogController } from 'components/eventLog';
import G_marketConfigStorage from 'components/globalMarketConfigStorage';
import { makeAssetBucket, UserInventoryStorageProvider } from 'components/internalLogic';
import { _I_, defineSimpleTerm } from 'components/locs';
import { ViewItemSearch } from 'components/marketedAssets';
import { ViewOrdersListContainer } from 'components/orderAssets';
import { spawnThemeTestModal } from 'components/themeTestDialog';
import { RealtimeTransport } from 'components/tickerListener';
import { ViewUserInventory } from 'components/userAssets';
import { spawnUserSettingsModal } from 'components/userSettingsDialog';
import WalletLimitsController from 'components/walletLimitsController';
import WalletWidgetIcon from 'components/walletWidgetIcon';
import { findLocaleCode, findLocaleIcon, getDefaultLocale } from 'generics/localeUtils';
import {
  Navbar,
  NavbarNav,
  NavbarNavDropdown,
  NavbarNavDropdownAction, NavbarNavGlyphiconLink
} from 'generics/navbar';
import { getStartArgs } from 'generics/queryArgs.js';
import { loadRoute, resetRoute } from 'generics/routed.js';
import React from 'react';
import ReactGA from 'react-ga';
import { GetGeneralSettings } from '../api/userSettingsAPICall';

LogStream.enable("MARKETAPP");

const handleRelogin = () => ApplicationEvents.emit_async("auth.goToRelogin", "MarketApp.Navbar relogin link");

class MarketApp extends React.Component {
  constructor(props) {
    super(props);

    this.appInventoryStorage = {};

    const settings = SettingsInjections.getUsedCircuit();
    if (settings.notify_server && settings.notify_realm)
      this.realtimeTransport = new RealtimeTransport(settings.notify_server,
        settings.notify_realm);


    this.setCurrentAssetByHash = (appid, hash_name) => this.setCurrentAsset(appid, hash_name);
    this.onUpdateInventoryReqBound = () => this.onUpdateInventoryReq();

    this.state = {
      "page": "market",
      "currentAsset": null,
    };

  }


  prepareStorage(appid) {
    if (this.appInventoryStorage[appid] === undefined)
      this.appInventoryStorage[appid] = new UserInventoryStorageProvider(appid);

    return this.appInventoryStorage[appid];
  }


  findAssetByClass(assetAppId, assetClassId, assetCtxId) {
    return this.prepareStorage(assetAppId).findAssetByClass(assetAppId, assetClassId, assetCtxId);
  }


  onUpdateInventoryReq() {
    Promise.all(this.props.appIds.map(
      (appid) => this.prepareStorage(appid).onUpdateInventoryReq()
    )).then(() => {
      ApplicationEvents.emit_async("market.inventoryUpdateCompleted", {});

      let { currentAsset } = this.state;
      if (currentAsset)
        ApplicationEvents.emit_async("market.setCurrentAssetByHash",
          currentAsset.appid, currentAsset.classInfo.market_hash_name);
    })
  }


  componentDidMount() {
    ApplicationEvents.on("market.setCurrentAssetByHash", this.setCurrentAssetByHash);
    ApplicationEvents.on("market.updateInventory", this.onUpdateInventoryReqBound);

    if (this.realtimeTransport)
      this.realtimeTransport.attach();

    ApplicationEvents.emit_async("market.updateInventory");

    const storedRoute = loadRoute();
    if (storedRoute.route && (storedRoute.route == "viewitem")) {
      const { app, market_hash_name } = (storedRoute.params || {});
      if (app && market_hash_name) {
        ApplicationEvents.emit_async("market.setCurrentAssetByHash", app, market_hash_name);
        resetRoute();
      }
    }

  }


  componentWillUnmount() {
    ApplicationEvents.off("market.setCurrentAssetByHash", this.setCurrentAssetByHash);
    ApplicationEvents.off("market.updateInventory", this.onUpdateInventoryReqBound);

    if (this.realtimeTransport)
      this.realtimeTransport.detach();
  }


  setCurrentAsset(appid, hash_name) {
    LogStream("MARKETAPP").info("setCurrentAssetByHash -> ", appid, hash_name);
    if ((!appid) || (!hash_name)) {
      resetRoute();
      this.setState({ "currentAsset": null });
      return;
    }

    refreshBlockedPairs(appid, hash_name);

    ClassByHashnameResolver.resolve(
      appid, hash_name

    ).then((assetclass) => {
      const classSid = ItemClassInfoResolver.getClassSid(appid, assetclass);
      LogStream("MARKETAPP").info(`Resolved ${appid}:"${hash_name}" => ${classSid} : `, assetclass);

      const asset = this.findAssetByClass(appid, classSid, null);

      if (asset) {
        LogStream("MARKETAPP").info("Found asset ", asset);
        this.setState({ "currentAsset": asset });
        return;
      }

      return ItemClassInfoResolver.resolve(
        appid, assetclass

      ).then((classInfo) => {
        let fakeAsset = makeAssetBucket(appid, assetclass, classInfo, null);
        LogStream("MARKETAPP").info("Fake asset ", fakeAsset);
        this.setState({ "currentAsset": fakeAsset });
        return;
      });

    }).catch((reason) => {
      this.setState({ "currentAsset": null });

      reason = Object.assign({}, reason,
        {
          "caption": "Asset Resolve Error",
          "message": ("Failed to resolve asset info:\n" +
            reason.message)
        });

      LogStream("MARKETAPP").error("setCurrentAssetByHash - Failed (", reason, ")");
      // ApplicationEvents.emit_async( "EventLog.append", reason );
    });
  }


  setPage(page) {
    this.setState({ "page": page, "currentAsset": null });
    ReactGA.pageview(page);
  }


  onShowThemeTestModal() {
    spawnThemeTestModal();
  }


  renderAppNavbar() {
    const currentPage = this.state.page;
    const { credentials } = this.props;
    const { steamAppId, steamId } = credentials;
    const steamArgs = (steamAppId && steamId) ? `?app_id=${steamAppId}&steam_id=${steamId}` : "";
    const replenishHref = `https://store.gaijin.net/balance.php${steamArgs}`;
    const balanceHistoryHref = `https://store.gaijin.net/balance_history.php${steamArgs}`;
    const profileHref = `https://store.gaijin.net/user.php${steamArgs}`;
    return (
      <Navbar>
        <NavbarNav>
          <NavbarNavGlyphiconLink icon="briefcase" active={currentPage == "inventory"}
            onClick={() => this.setPage("inventory")}  >
            {_I_("MarketApp.Navbar.inventoryLink")}
          </NavbarNavGlyphiconLink>

          <NavbarNavGlyphiconLink icon="list" active={currentPage == "orders"}
            onClick={() => this.setPage("orders")}  >
            {_I_("MarketApp.Navbar.ordersLink")}
          </NavbarNavGlyphiconLink>

          <NavbarNavGlyphiconLink icon="shopping-cart" active={currentPage == "market"}
            onClick={() => this.setPage("market")}  >
            {_I_("MarketApp.Navbar.marketLink")}
          </NavbarNavGlyphiconLink>
        </NavbarNav>

        <NavbarNav right>

          <NavbarNavDropdown
            caption={(
              <WalletWidgetIcon />
            )}  >

            <NavbarNavDropdownAction href={replenishHref} newtab  >
              {_I_("MarketApp.Navbar.replenishLink")}</NavbarNavDropdownAction>

            <NavbarNavDropdownAction href={balanceHistoryHref} newtab  >
              {_I_("MarketApp.Navbar.balanceHistoryLink")}</NavbarNavDropdownAction>

          </NavbarNavDropdown>

          <NavbarNavDropdown
            caption={(
              <CredentialsIcon creds={credentials} />
            )}  >

            <NavbarNavDropdownAction onClick={spawnUserSettingsModal}  >
              {_I_("MarketApp.Navbar.settingsLink")}</NavbarNavDropdownAction>

            <NavbarNavDropdownAction href={profileHref} newtab  >
              {_I_("MarketApp.Navbar.profileLink")}</NavbarNavDropdownAction>

            <NavbarNavDropdownAction onClick={handleRelogin}  >
              {_I_("MarketApp.Navbar.reloginLink")}</NavbarNavDropdownAction>

            <NavbarNavDropdownAction onClick={() => this.onShowThemeTestModal()}
              hidden={!this.props.siteSettings.enableThemeTests}  >
              Theme Testing</NavbarNavDropdownAction>

          </NavbarNavDropdown>
        </NavbarNav>
      </Navbar>
    );
  }


  renderTradeDialog() {
    let { currentAsset } = this.state;
    if (currentAsset)
      return (
        <ViewTradeDialog
          classInfo={currentAsset.classInfo}
          assetClass={currentAsset.assetClass}
          instances={currentAsset.instances}
          ctxid={currentAsset.ctxid}
          appid={currentAsset.appid}
          totalAmount={currentAsset.totalAmount}
        />
      );
  }


  renderOrders() {
    const hiddenPage = (this.state.currentAsset ||
      (this.state.page != "orders")) ? "hidden" : "";
    return (
      <div className={hiddenPage}>
        <ViewOrdersListContainer />
      </div>
    );
  }


  renderMarket() {
    const hiddenPage = (this.state.currentAsset ||
      (this.state.page != "market")) ? "hidden" : "";
    return (
      <div className={hiddenPage}>
        <ViewItemSearch appIds={this.props.appIds} />
      </div>
    );
  }


  renderInventory() {
    const hiddenPage = (this.state.currentAsset ||
      (this.state.page != "inventory")) ? "hidden" : "";
    return (
      <div className={hiddenPage}>
        <ViewUserInventory />
      </div>
    );
  }


  render() {
    return (
      <div className="view-market-app-main">
        <WalletLimitsController />
        {this.renderAppNavbar()}
        {this.renderTradeDialog()}
        {this.renderOrders()}
        {this.renderMarket()}
        {this.renderInventory()}
      </div>
    );
  }

}


export class MarketConfigLoader extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      appIds: null
    };
  }

  loadAppIds() {
    return G_marketConfigStorage.load(
    ).catch((error) => {
      console.warn("loadAppIds", error);
    });
  }

  setupUsedLocale() {
    const { skin_lang: icon } = getStartArgs();
    const lang = findLocaleIcon(icon);
    if (lang)
      utils_api.setStored(["MarketApp", "LRU", "locale"], lang.code);

    const selectedLocale = findLocaleCode(utils_api.getStored(["MarketApp", "LRU", "locale"]));
    if (selectedLocale && selectedLocale.enabled) {
      SettingsInjections._Locale = selectedLocale;
      console.info(`usedLocale loaded successfully: ${selectedLocale.code}`);
    } else {
      SettingsInjections._Locale = getDefaultLocale();
      console.info(`usedLocale defaulted to: ${SettingsInjections._Locale.code}`);
    }
  }

  loadSettings() {
    return GetGeneralSettings()
      .then((settings) => {
        console.log("loadSettings", settings);

        utils_api.setStored(["MarketApp", "cloudSettings"], settings);

        if ('locale' in settings)
          utils_api.setStored(["MarketApp", "LRU", "locale"], settings.locale);

        this.setupUsedLocale();

        if ('privateTradingState' in settings)
          utils_api.setStored(["MarketApp", "privateTradingState"], settings.privateTradingState);

        if ('interfaceOldVersion' in settings)
          utils_api.setStored(["MarketApp", "interfaceOldVersion"], settings.interfaceOldVersion);
      })
      .catch((error) => {
        console.warn("loadSettings", error);
      })
  }

  componentDidMount() {

    Promise.all([
      this.loadSettings(),
      this.loadAppIds()
    ]).then(() => {
      this.setState({ appIds: G_marketConfigStorage.getAppIdList() });
    }).catch((error) => {
      console.warn("MarketConfigLoader", error);
    });
  }


  componentWillUnmount() {
  }


  render() {
    if (!this.state.appIds)
      return null;

    return (
      <div>
        <MarketApp
          credentials={this.props.credentials}
          siteSettings={this.props.siteSettings}
          appIds={this.state.appIds}
        />

        <DialogHolder />
        <EventLogController
          token={(this.props.credentials || {}).token}
          devMode={this.props.devMode}
        />
      </div>
    );
  }

}


defineSimpleTerm("MarketApp.Navbar.inventoryLink", "Inventory", {
  "ru_RU": "Инвентарь",
  "cs_CZ": "Sklad",
  "fr_FR": "Inventaire",
  "de_DE": "Inventar",
  "it_IT": "Inventario",
  "ko_KR": "보관함",
  "pl_PL": "Ekwipunek",
  "pt_PT": "Inventory",
  "es_ES": "Inventario",
  "zh_CN": "道具清单",
  "zh_TW": "道具清單"
});

defineSimpleTerm("MarketApp.Navbar.ordersLink", "My orders", {
  "ru_RU": "Ордера",
  "cs_CZ": "Moje objednávky",
  "fr_FR": "Mes commandes",
  "de_DE": "Meine Bestellungen",
  "it_IT": "I miei ordini",
  "ko_KR": "내 주문 목록",
  "pl_PL": "Moje transakcje",
  "pt_PT": "My orders",
  "es_ES": "Mis pedidos",
  "zh_CN": "我的订单",
  "zh_TW": "我的訂單"
});

defineSimpleTerm("MarketApp.Navbar.marketLink", "Market", {
  "ru_RU": "Биржа",
  "cs_CZ": "Trh",
  "fr_FR": "Marché",
  "de_DE": "Market",
  "it_IT": "Mercato",
  "ko_KR": "마켓",
  "pl_PL": "Rynek",
  "pt_PT": "Market",
  "es_ES": "Mercado",
  "zh_CN": "市场",
  "zh_TW": "市場"
});

defineSimpleTerm("MarketApp.Navbar.settingsLink", "Settings", {
  "ru_RU": "Настройки",
  "cs_CZ": "Nastavení",
  "fr_FR": "Paramètres",
  "de_DE": "Einstellungen",
  "it_IT": "Impostazioni",
  "ko_KR": "설정",
  "pl_PL": "Ustawienia",
  "pt_PT": "Settings",
  "es_ES": "Ajustes",
  "zh_CN": "设置",
  "zh_TW": "設定"
});

defineSimpleTerm("MarketApp.Navbar.replenishLink", "Replenish balance", {
  "ru_RU": "Пополнить баланс",
  "cs_CZ": "Doplnit zůstatek",
  "fr_FR": "Créditer le compte",
  "de_DE": "Guthaben auffüllen",
  "it_IT": "Rifornisci il bilancio",
  "ko_KR": "코인 충전",
  "pl_PL": "Uzupełnij konto",
  "pt_PT": "Replenish balance",
  "es_ES": "Reponer balance",
  "zh_CN": "充值余额",
  "zh_TW": "儲值餘額"
});

defineSimpleTerm("MarketApp.Navbar.balanceHistoryLink", "Balance history", {
  "ru_RU": "История баланса",
  "cs_CZ": "Historie zůstatku",
  "fr_FR": "Historique du compte",
  "de_DE": "Guthabenverlauf",
  "it_IT": "Storico del bilancio",
  "ko_KR": "코인 이용 내역",
  "pl_PL": "Historia konta",
  "pt_PT": "Balance history",
  "es_ES": "Historial de balance",
  "zh_CN": "余额历史",
  "zh_TW": "儲值歷史"
});

defineSimpleTerm("MarketApp.Navbar.buyCoinsLink", "Buy GaijinCoins", {
  "ru_RU": "Купить GaijinCoin'ы",
  "cs_CZ": "Koupit GaijinCoins",
  "fr_FR": "Acheter des GaijinCoins",
  "de_DE": "GaijinCoins kaufen",
  "it_IT": "Compra GaijinCoins",
  "ko_KR": "가이진 코인 구매",
  "pl_PL": "Kup GaijinCoins",
  "pt_PT": "Buy GaijinCoins",
  "es_ES": "Compra GaijinCoins",
  "zh_CN": "购买 Gaijin币",
  "zh_TW": "購買 Gaijin幣"
});

defineSimpleTerm("MarketApp.Navbar.profileLink", "Profile", {
  "ru_RU": "Профиль",
  "cs_CZ": "Profil",
  "fr_FR": "Profil",
  "de_DE": "Profil",
  "it_IT": "Profilo",
  "ko_KR": "프로필",
  "pl_PL": "Profil",
  "pt_PT": "Profile",
  "es_ES": "Perfil",
  "zh_CN": "档案",
  "zh_TW": "檔案"
});

defineSimpleTerm("MarketApp.Navbar.reloginLink", "Relogin or change account", {
  "ru_RU": "Сменить аккаунт",
  "cs_CZ": "Odhlásit nebo změnit účet",
  "fr_FR": "Se reconnecter ou changer de compte",
  "de_DE": "Neu einloggen oder Konto wechseln",
  "it_IT": "Rilogga o cambia account",
  "ko_KR": "재로그인 혹은 계정 변경",
  "pl_PL": "Zaloguj ponownie lub zmień konto",
  "pt_PT": "Relogin or change account",
  "es_ES": "Inicia sesión de nuevo o cambia de cuenta",
  "zh_CN": "重新登录或更改帐户",
  "zh_TW": "重新登入或更改帳戶"
});

